import React, { useState } from 'react';
import Navbar from '../components/sub-comp/Navbar.jsx';
import Footer from '../components/sub-comp/MainFooter.jsx';
import FloatingSocialPanels from '../components/sub-comp/FloatingSocialPanels';
import DegreeProgramsAd from '../components/Home/DegreeProgramsAd';
import DegreeProgramsAdEnhanced from '../components/Home/DegreeProgramsAdEnhanced';
import DegreeProgramsBanner from '../components/Home/DegreeProgramsBanner';
import { FaPlay, FaStop, FaEye, FaCode, FaRocket } from 'react-icons/fa';

const AdDemo = () => {
  const [activeAds, setActiveAds] = useState({
    basic: false,
    enhanced: false,
    banner: false
  });

  const [adSettings, setAdSettings] = useState({
    position: 'top-right',
    delay: 2000,
    autoClose: false,
    autoCloseDelay: 10000,
    variant: 'top'
  });

  const toggleAd = (adType) => {
    setActiveAds(prev => ({
      ...prev,
      [adType]: !prev[adType]
    }));
  };

  const resetAllAds = () => {
    setActiveAds({
      basic: false,
      enhanced: false,
      banner: false
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Degree Programs Advertisement Demo
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Interactive showcase of flash-like advertisements for degree programs
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <FaEye className="text-2xl" />
              <span>Interactive Demo</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <FaCode className="text-2xl" />
              <span>Multiple Variants</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <FaRocket className="text-2xl" />
              <span>Click to Navigate</span>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Controls */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Advertisement Controls
            </h2>
            <p className="text-lg text-gray-600">
              Test different advertisement variants and configurations
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Basic Ad */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Basic Advertisement</h3>
                <p className="text-gray-600 text-sm">Simple floating ad with basic animations</p>
              </div>
              <button
                onClick={() => toggleAd('basic')}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                  activeAds.basic 
                    ? 'bg-red-500 text-white hover:bg-red-600' 
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                {activeAds.basic ? <FaStop className="inline mr-2" /> : <FaPlay className="inline mr-2" />}
                {activeAds.basic ? 'Stop Basic Ad' : 'Show Basic Ad'}
              </button>
            </div>

            {/* Enhanced Ad */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Enhanced Advertisement</h3>
                <p className="text-gray-600 text-sm">Advanced ad with rotating content and icons</p>
              </div>
              <button
                onClick={() => toggleAd('enhanced')}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                  activeAds.enhanced 
                    ? 'bg-red-500 text-white hover:bg-red-600' 
                    : 'bg-pink-500 text-white hover:bg-pink-600'
                }`}
              >
                {activeAds.enhanced ? <FaStop className="inline mr-2" /> : <FaPlay className="inline mr-2" />}
                {activeAds.enhanced ? 'Stop Enhanced Ad' : 'Show Enhanced Ad'}
              </button>
            </div>

            {/* Banner Ad */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Banner Advertisement</h3>
                <p className="text-gray-600 text-sm">Full-width banner style advertisement</p>
              </div>
              <button
                onClick={() => toggleAd('banner')}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                  activeAds.banner 
                    ? 'bg-red-500 text-white hover:bg-red-600' 
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {activeAds.banner ? <FaStop className="inline mr-2" /> : <FaPlay className="inline mr-2" />}
                {activeAds.banner ? 'Stop Banner Ad' : 'Show Banner Ad'}
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Advertisement Settings</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Position</label>
                <select
                  value={adSettings.position}
                  onChange={(e) => setAdSettings(prev => ({...prev, position: e.target.value}))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="top-right">Top Right</option>
                  <option value="top-left">Top Left</option>
                  <option value="bottom-right">Bottom Right</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="center">Center</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Delay (ms)</label>
                <input
                  type="number"
                  value={adSettings.delay}
                  onChange={(e) => setAdSettings(prev => ({...prev, delay: parseInt(e.target.value)}))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="0"
                  max="10000"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Auto Close</label>
                <select
                  value={adSettings.autoClose}
                  onChange={(e) => setAdSettings(prev => ({...prev, autoClose: e.target.value === 'true'}))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="false">Disabled</option>
                  <option value="true">Enabled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Auto Close Delay (ms)</label>
                <input
                  type="number"
                  value={adSettings.autoCloseDelay}
                  onChange={(e) => setAdSettings(prev => ({...prev, autoCloseDelay: parseInt(e.target.value)}))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1000"
                  max="60000"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-4">
              <button
                onClick={resetAllAds}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-semibold"
              >
                Reset All Ads
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Advertisement Features
            </h2>
            <p className="text-lg text-gray-600">
              Key features of our flash-like advertisements
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-purple-100 text-purple-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <FaRocket className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Eye-Catching Animations</h3>
              <p className="text-gray-600">Pulsing, bouncing, and shimmering effects to grab attention</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-pink-100 text-pink-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <FaCode className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Dynamic Content</h3>
              <p className="text-gray-600">Rotating text and icons to keep content fresh and engaging</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-blue-100 text-blue-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <FaEye className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Responsive Design</h3>
              <p className="text-gray-600">Works perfectly on desktop, tablet, and mobile devices</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-green-100 text-green-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <FaPlay className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Easy Navigation</h3>
              <p className="text-gray-600">One-click navigation to the degree programs page</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-yellow-100 text-yellow-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <FaStop className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">User Control</h3>
              <p className="text-gray-600">Users can close ads manually or set auto-close timers</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-red-100 text-red-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <FaRocket className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Customizable</h3>
              <p className="text-gray-600">Multiple positions, delays, and auto-close options</p>
            </div>
          </div>
        </div>
      </section>

      {/* Active Advertisements */}
      {activeAds.basic && (
        <DegreeProgramsAd />
      )}
      
      {activeAds.enhanced && (
        <DegreeProgramsAdEnhanced 
          position={adSettings.position}
          delay={adSettings.delay}
          autoClose={adSettings.autoClose}
          autoCloseDelay={adSettings.autoCloseDelay}
        />
      )}
      
      {activeAds.banner && (
        <DegreeProgramsBanner 
          variant={adSettings.variant}
          autoClose={adSettings.autoClose}
          autoCloseDelay={adSettings.autoCloseDelay}
        />
      )}

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default AdDemo;
